import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetBillingCharges } from "../../../../api/masters/billingChargesAPI";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetIpdBillChargeById, UpdateIpdBillCharge } from "../../../../api/ipd/ipdBillChargesAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetDeliveryById, UpdateDelivery } from "../../../../api/ipd/deliveryAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
    ipdEntryId: yup
        .string()
        .required("IPD Entry is required"),

    vNo: yup
        .number()
        .typeError("V No must be a number")
        .required("V No is required"),

    doctorId1: yup
        .string()
        .required("Doctor 1 is required"),

    doctorId2: yup
        .string()
        .nullable(),

    aneasthetistId: yup
        .string()
        .nullable(),

    assistantId: yup
        .string()
        .nullable(),

    deliveryDate: yup
        .date()
        .required("Delivery date is required"),

    type: yup
        .string()
        .required("Type is required"),
    patientStatus: yup
        .string()
        .required("Patient status is required"),

    pregancyInWeek: yup
        .number()
        .typeError("Pregnancy week must be a number")
        .nullable(),

    bornChild: yup
        .number()
        .typeError("Born child must be a number")
        .required("Born child is required"),

    // aliveChild: yup.number()
    //     .typeError("Alive child must be a number")
    //     .nullable(),

    // deadChild: yup
    //     .number()
    //     .typeError("Dead child must be a number")
    //     .nullable(),

    // lastChildAge: yup
    //     .number()
    //     .typeError("Last child age must be a number")
    //     .nullable(),

    // lastChildSex: yup
    //     .string()
    //     .nullable(),

    // totalLivingChild: yup
    //     .number()
    //     .typeError("Total living child must be a number")
    //     .nullable(),

    childDetails: yup.array().of(
        yup.object().shape({
            name: yup.string().nullable(),
            sex: yup.string().required("Sex is required"),
            weight: yup
                .number()
                .typeError("Weight must be a number")
                .nullable(),
            unit: yup.string().nullable(),
            time: yup.string().required("Time is required"),
            date: yup.date().required("Date is required")
        })
    ),

    remark: yup
        .string()
        .nullable(),

    monther: yup.object().shape({
        ageAtMarriage: yup
            .number()
            .typeError("Age at marriage must be a number")
            .nullable(),
        nationality: yup.string().nullable(),
        religion: yup.string().nullable(),
        qualification: yup.string().nullable(),
        occupation: yup.string().nullable()
    }),

    father: yup.object().shape({
        fatherName: yup.string().nullable(),
        age: yup
            .number()
            .typeError("Father age must be a number")
            .nullable(),
        nationality: yup.string().nullable(),
        religion: yup.string().nullable(),
        qualification: yup.string().nullable(),
        occupation: yup.string().nullable()
    }),
});

const EditDeliveryEntry = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();
	const [ipdEntries, setIpdEntries] = useState([]);
	const [doctors, setDoctors] = useState([]);
	const [patientDetails, setPatientDetails] = useState({});

	const {
		register,
		handleSubmit,
		control,
		setValue,
		formState: { errors }
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			childDetails: [{}],
			monther: {},
			father: {}
		}
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "childDetails"
	});

	const fetchIpdEntries = async () => {
		try {
			const response = await GetIpdEntries();
			if (response.success) {
				setIpdEntries(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchDoctors = async () => {
		try {
			const response = await GetConsultants();
			if (response.success) {
				setDoctors(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchData = async () => {
		try {
			const response = await GetDeliveryById(id); // Make sure login function returns token
			if (response.success) {
				Object.entries(response.data).forEach(([key, value]) => {
					// Handle main object dates
					if (["date","deliveryDate"].includes(key) && value) {
						setValue(key, value.split("T")[0]);
						return;
					}

					// Handle billingChanges array
					if (key === "childDetails" && Array.isArray(value)) {
						value.forEach((item, index) => {
							Object.entries(item).forEach(([itemKey, itemValue]) => {
								if (itemKey === "date" && itemValue) {
									setValue(`childDetails.${index}.${itemKey}`, itemValue.split("T")[0]);
								} else {
									setValue(`childDetails.${index}.${itemKey}`, itemValue);
								}
							});
						});
						return;
					}

					// Default
					setValue(key, value);
				});

				setPatientDetails(response.data?.ipdEntryId || {});
				setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');
				setValue('doctorId1', response.data?.doctorId1?._id || '');
				setValue('doctorId2', response.data?.doctorId2?._id || '');
				setValue('aneasthetistId', response.data?.aneasthetistId?._id || '');
				setValue('assistantId', response.data?.assistantId?._id || '');
                

			} else {
				// setError(response.message);
				showToast("error", response.message);
			}
		} catch (error) {
			// setError(error); // Handle login errors
			showToast("error", error);
		}
	};

	// Fetch existing data
	useEffect(
		() => {
			if (id) {
				fetchIpdEntries().then(() => {
					fetchDoctors().then(() => {
						fetchData();
					});

				});
			}
		},
		[id, setValue]
	);

	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });

			const response = await UpdateDelivery(id, data);
			if (response.success) {
				showToast("success", response.message);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

    const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

	return (
		<Fragment>
			<Breadcrumb parent="Delivery" title="Edit Delivery Entry" />
			<div className="container-fluid">
				<div className="row">
					<div className="col-sm-12">
						<div className="card">
							<div className="card-header d-flex justify-content-between">
								<h5>Edit Delivery Entry</h5>
								<Link
									to={`${process.env
										.PUBLIC_URL}/ipd/transaction/delivery-entry/list`}
								>
									<button className="btn btn-primary" id="list">
										List Delivery Entry
									</button>
									<UncontrolledTooltip placement="top" target="list">
										List Delivery Entry
									</UncontrolledTooltip>
								</Link>
							</div>

							<form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">

                                    {/* ================= IPD & BASIC ================= */}
                                    <h5 className="text-primary mb-3">IPD & Delivery Info</h5>

                                    <div className="row">
                                        <div className="col-6">
                                            <label>Room / IPD *</label>
                                            <select className="form-control" {...register("ipdEntryId", {
                                                onChange: (e) => {
                                                    setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                }
                                            })}>
                                                <option value="">Select IPD</option>
                                                {ipdEntries.map(ipd => (
                                                    <option key={ipd._id} value={ipd._id}>
                                                        {ipd.roomId?.roomNo}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                                        </div>

                                        <div className="col-6">
                                            <label>V No *</label>
                                            <input type="number" className="form-control" {...register("vNo")} readOnly />
                                            {errors.vNo && <span className="text-danger">{errors.vNo.message}</span>}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* ================= DOCTORS ================= */}
                                    <div className="row mt-3">
                                        <div className="col-6">
                                            <label>Consultant Doctor 1 *</label>
                                            <select className="form-control" {...register("doctorId1")}>
                                                <option value="">Select Doctor</option>
                                                {doctors.map(doc => (
                                                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                                                ))}
                                            </select>
                                            {errors.doctorId1 && <span className="text-danger">{errors.doctorId1.message}</span>}
                                        </div>

                                        <div className="col-6">
                                            <label>Consultant Doctor 2</label>
                                            <select className="form-control" {...register("doctorId2")}>
                                                <option value="">Select Doctor</option>
                                                {doctors.map(doc => (
                                                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                                                ))}
                                            </select>
                                            {errors.doctorId2 && <span className="text-danger">{errors.doctorId2.message}</span>}
                                        </div>
                                    </div>

                                    {/* ================= DELIVERY DETAILS ================= */}
                                    <div className="row mt-3">
                                        <div className="col-4">
                                            <label>Anesthetist</label>
                                            <select className="form-control" {...register("aneasthetistId")}>
                                                <option value="">Select Anesthetist</option>
                                                {doctors.map(doc => (
                                                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                                                ))}
                                            </select>
                                            {errors.aneasthetistId && <span className="text-danger">{errors.aneasthetistId.message}</span>}
                                        </div>

                                        <div className="col-4">
                                            <label>Assistant</label>
                                            <select className="form-control" {...register("assistantId")}>
                                                <option value="">Select Doctor</option>
                                                {doctors.map(doc => (
                                                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                                                ))}
                                            </select>
                                            {errors.assistantId && <span className="text-danger">{errors.assistantId.message}</span>}
                                        </div>

                                        <div className="col-4">
                                            <label>Delivery Date *</label>
                                            <input type="date" className="form-control" {...register("deliveryDate")} />
                                            {errors.deliveryDate && <span className="text-danger">{errors.deliveryDate.message}</span>}
                                        </div>

                                        <div className="col-4 mt-2">
                                            <label>Delivery Type *</label>
                                            <select className="form-control" {...register("type")} >
                                                <option value="Normal">Normal</option>
                                                <option value="Caesarian">Caesarian</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            {errors.type && <span className="text-danger">{errors.type.message}</span>}
                                        </div>

                                        <div className="col-4 mt-2">
                                            <label>Patient Status *</label>
                                            <select className="form-control" {...register("patientStatus")} >
                                                <option value="Normal">Normal</option>
                                                <option value="Critical">Critical</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            {errors.patientStatus && <span className="text-danger">{errors.patientStatus.message}</span>}
                                        </div>

                                        <div className="col-4 mt-2">
                                            <label>Pregnancy (Weeks)</label>
                                            <input type="number" className="form-control" {...register("pregancyInWeek")} />
                                            {errors.pregancyInWeek && <span className="text-danger">{errors.pregancyInWeek.message}</span>}
                                        </div>
                                    </div>

                                    {/* ================= CHILD SUMMARY ================= */}
                                    <h5 className="text-primary mt-4">Child Summary</h5>

                                    <div className="row">
                                        <div className="col-3">
                                            <label>Born Child *</label>
                                            <input type="number" className="form-control" {...register("bornChild")} />
                                            {errors.bornChild && <span className="text-danger">{errors.bornChild.message}</span>}
                                        </div>

                                        <div className="col-3">
                                            <label>Alive Child</label>
                                            <input type="number" className="form-control" {...register("aliveChild")} />
                                            {errors.aliveChild && <span className="text-danger">{errors.aliveChild.message}</span>}
                                        </div>

                                        <div className="col-3">
                                            <label>Dead Child</label>
                                            <input type="number" className="form-control" {...register("deadChild")} />
                                            {errors.deadChild && <span className="text-danger">{errors.deadChild.message}</span>}
                                        </div>

                                        <div className="col-3">
                                            <label>Total Living Child</label>
                                            <input type="number" className="form-control" {...register("totalLivingChild")} />
                                            {errors.totalLivingChild && <span className="text-danger">{errors.totalLivingChild.message}</span>}
                                        </div>
                                    </div>

                                    <div className="row mt-2">
                                        <div className="col-6">
                                            <label>Last Child Age</label>
                                            <input type="number" className="form-control" {...register("lastChildAge")} />
                                            {errors.lastChildAge && <span className="text-danger">{errors.lastChildAge.message}</span>}
                                        </div>

                                        <div className="col-6">
                                            <label>Last Child Sex</label>
                                            <select className="form-control" {...register("lastChildSex")} >
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                            </select>
                                            {errors.lastChildSex && <span className="text-danger">{errors.lastChildSex.message}</span>}
                                        </div>
                                    </div>

                                    {/* ================= CHILD DETAILS ================= */}
                                    <h5 className="text-primary mt-4">Child Details</h5>

                                    {fields.map((field, index) => (
                                        <div className="row border p-2 mb-2" key={field.id}>
                                            <div className="col-2">
                                                <label>Child Name</label>
                                                <input placeholder="Name" className="form-control"
                                                    {...register(`childDetails.${index}.name`)} />
                                                {errors.childDetails?.[index]?.name && <span className="text-danger">{errors.childDetails?.[index]?.name.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Sex</label>
                                                <select placeholder="Sex" className="form-control"
                                                    {...register(`childDetails.${index}.sex`)} >
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                </select>
                                                {errors.childDetails?.[index]?.sex && <span className="text-danger">{errors.childDetails?.[index]?.sex.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Weight</label>
                                                <input type="number" placeholder="Weight" className="form-control"
                                                    {...register(`childDetails.${index}.weight`)} />
                                                {errors.childDetails?.[index]?.weight && <span className="text-danger">{errors.childDetails?.[index]?.weight.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Unit</label>
                                                <input placeholder="Unit" className="form-control"
                                                    {...register(`childDetails.${index}.unit`)} />
                                                {errors.childDetails?.[index]?.unit && <span className="text-danger">{errors.childDetails?.[index]?.unit.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Time</label>
                                                <input type="time" className="form-control"
                                                    {...register(`childDetails.${index}.time`)} />
                                                {errors.childDetails?.[index]?.time && <span className="text-danger">{errors.childDetails?.[index]?.time.message}</span>}
                                            </div>

                                            <div className="col-2">
                                                <label>Date</label>
                                                <input type="date" className="form-control"
                                                    {...register(`childDetails.${index}.date`)} />
                                                {errors.childDetails?.[index]?.date && <span className="text-danger">{errors.childDetails?.[index]?.date.message}</span>}
                                            </div>

                                            <div className="col-12 mt-2 text-end">
                                                <button type="button" className="btn btn-danger btn-sm"
                                                    onClick={() => remove(index)}>
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    <button
                                        type="button"
                                        className="btn btn-secondary mt-2"
                                        onClick={() => append({})}
                                    >
                                        + Add Child
                                    </button>

                                    {/* ================= MOTHER ================= */}
                                    <h5 className="text-primary mt-4">Mother Details</h5>

                                    <div className="row">
                                        <div className="col-4">
                                            <label>Age at Marriage</label>
                                            <input placeholder="Age at Marriage" type="number"
                                                className="form-control"
                                                {...register("monther.ageAtMarriage")} />
                                            {errors.monther?.ageAtMarriage && <span className="text-danger">{errors.monther?.ageAtMarriage.message}</span>}
                                        </div>
                                        <div className="col-4">
                                            <label>Nationality</label>
                                            <input placeholder="Nationality" className="form-control"
                                                {...register("monther.nationality")} />
                                            {errors.monther?.nationality && <span className="text-danger">{errors.monther?.nationality.message}</span>}
                                        </div>
                                        <div className="col-4">
                                            <label>Religion</label>
                                            <input placeholder="Religion" className="form-control"
                                                {...register("monther.religion")} />
                                            {errors.monther?.religion && <span className="text-danger">{errors.monther?.religion.message}</span>}
                                        </div>
                                        <div className="col-6 mt-2">
                                            <label>Qualification</label>
                                            <input placeholder="Qualification" className="form-control"
                                                {...register("monther.qualification")} />
                                            {errors.monther?.qualification && <span className="text-danger">{errors.monther?.qualification.message}</span>}
                                        </div>
                                        <div className="col-6 mt-2">
                                            <label>Occupation</label>
                                            <input placeholder="Occupation" className="form-control"
                                                {...register("monther.occupation")} />
                                            {errors.monther?.occupation && <span className="text-danger">{errors.monther?.occupation.message}</span>}
                                        </div>
                                    </div>

                                    {/* ================= FATHER ================= */}
                                    <h5 className="text-primary mt-4">Father Details</h5>

                                    <div className="row">
                                        <div className="col-4">
                                            <label>Father Name</label>
                                            <input placeholder="Father Name" className="form-control"
                                                {...register("father.fatherName")} />
                                            {errors.father?.fatherName && <span className="text-danger">{errors.father?.fatherName.message}</span>}
                                        </div>
                                        <div className="col-4">
                                            <label>Age</label>
                                            <input placeholder="Age" type="number" className="form-control"
                                                {...register("father.age")} />
                                            {errors.father?.age && <span className="text-danger">{errors.father?.age.message}</span>}
                                        </div>
                                        <div className="col-4">
                                            <label>Nationality</label>
                                            <input placeholder="Nationality" className="form-control"
                                                {...register("father.nationality")} />
                                            {errors.father?.nationality && <span className="text-danger">{errors.father?.nationality.message}</span>}
                                        </div>
                                        <div className="col-6 mt-2">
                                            <label>Qualification</label>
                                            <input placeholder="qualification" className="form-control"
                                                {...register("father.qualification")} />
                                            {errors.father?.qualification && <span className="text-danger">{errors.father?.qualification.message}</span>}
                                        </div>
                                        <div className="col-6 mt-2">
                                            <label>Occupation</label>
                                            <input placeholder="Occupation" className="form-control"
                                                {...register("father.occupation")} />
                                            {errors.father?.occupation && <span className="text-danger">{errors.father?.occupation.message}</span>}
                                        </div>
                                    </div>

                                    {/* ================= REMARK ================= */}
                                    <div className="mt-4">
                                        <label>Remark</label>
                                        <textarea className="form-control" {...register("remark")} />
                                        {errors["remark"] && <span className="text-danger">{errors["remark"].message}</span>}
                                    </div>

                                </div>

                                <div className="card-footer text-end">
                                    <button type="submit" className="btn btn-primary">
                                        Submit Delivery
                                    </button>
                                </div>
                            </form>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default EditDeliveryEntry;
